import {
	AlertTriangle,
	CalendarClock,
	CheckCircle2,
	ClipboardList,
	FileSearch,
	GitCompareArrows,
	History,
	PlayCircle,
	RefreshCw,
	Save,
	Wrench,
	XCircle,
} from "lucide-react";
import { useState } from "react";

import {
	EvalSampleProjection,
	type EvalSampleProjectionContext,
	buildEvalReplayHref,
} from "@/components/eval-shared/eval-sample-projection";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import type {
	BadcaseEventItem,
	BadcaseItem,
	BadcaseUpdateRequest,
	EvalRegressionBatchItem,
	EvalRunCompareResponse,
	EvalRunCompareSnapshotItem,
	EvalRunItem,
} from "@/lib/api/eval";
import {
	DEFAULT_EVAL_MERCHANT_ID,
	useAutoCloseBadcase,
	useBadcaseEvents,
	useBadcases,
	useCloseRegressionBatchBadcases,
	useEvalRunCompare,
	useEvalRunCompareSnapshots,
	useEvalRuns,
	useExecuteBadcaseRerun,
	useExecuteRegressionBatch,
	usePersistEvalRunCompare,
	useRecordBadcaseRerun,
	useRegressionBatches,
	useScheduleRegressionBatch,
	useUpdateBadcase,
	useWritebackBadcaseRegression,
} from "@/lib/api/eval";

const RUN_STATUS_LABEL: Record<EvalRunItem["status"], string> = {
	running: "运行中",
	completed: "已完成",
	failed: "失败",
};

const BADCASE_STATUS_LABEL: Record<BadcaseItem["status"], string> = {
	candidate: "待确认",
	confirmed: "已确认",
	fixed: "已修复",
	graduated: "已写回",
	closed: "已关闭",
};

const RERUN_STATUS_LABEL: Record<BadcaseItem["rerun_status"], string> = {
	not_rerun: "待复跑",
	rerun_passed: "复跑通过",
	rerun_failed: "复跑仍失败",
};

const REGRESSION_BATCH_STATUS_LABEL: Record<EvalRegressionBatchItem["status"], string> = {
	scheduled: "待执行",
	running: "执行中",
	completed: "已完成",
	failed: "失败",
};

const BADCASE_EVENT_LABEL: Record<BadcaseEventItem["event_type"], string> = {
	candidate_created: "候选创建",
	status_changed: "状态变更",
	rerun_recorded: "复跑登记",
	graduated: "毕业登记",
	regression_writeback: "回归写回",
};

const ROOT_CAUSE_LABEL: Record<BadcaseItem["root_cause_category"], string> = {
	intent_error: "意图识别偏差",
	playbook_error: "方案选择偏差",
	tool_error: "工具事实偏差",
	reply_error: "回复表达偏差",
	quality_gate_error: "质量门禁偏差",
	data_quality_error: "样本数据偏差",
	unknown: "待人工确认",
};

export function EvalLedgers() {
	return (
		<div className="space-y-4">
			<div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
				<EvalRunLedger />
				<BadcaseLedger />
			</div>
			<EvalRunComparePanel />
			<RegressionBatchPanel />
		</div>
	);
}

export function EvalRunLedger() {
	const runs = useEvalRuns();
	const items = runs.data?.items ?? [];

	return (
		<Card data-testid="m07-run-ledger">
			<CardHeader className="space-y-2">
				<div className="flex items-start gap-3">
					<span className="flex h-9 w-9 flex-none items-center justify-center rounded-md bg-surface-subtle text-brand-primary">
						<History className="h-4 w-4" aria-hidden="true" />
					</span>
					<div>
						<CardTitle className="text-lg">评测运行历史</CardTitle>
						<CardDescription className="mt-1 leading-6">
							把每次回归沉淀为可追溯的运营基线，先看可信度、样本边界和失败规模。
						</CardDescription>
					</div>
				</div>
			</CardHeader>
			<CardContent>
				{runs.isLoading ? (
					<p className="text-sm text-text-muted">加载运行历史…</p>
				) : runs.isError ? (
					<p className="text-sm text-brand-danger">评测运行历史加载失败</p>
				) : items.length === 0 ? (
					<p className="rounded-md border border-dashed border-surface-border px-3 py-5 text-sm text-text-muted">
						暂无留痕运行。先跑一次回归，再沉淀为基线。
					</p>
				) : (
					<div className="space-y-3">
						{items.map((run) => (
							<RunRow key={run.eval_run_id} run={run} />
						))}
					</div>
				)}
			</CardContent>
		</Card>
	);
}

function RunRow({ run }: { run: EvalRunItem }) {
	return (
		<article className="rounded-md border border-surface-border bg-surface-background px-3 py-3">
			<div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
				<div className="min-w-0">
					<div className="flex flex-wrap items-center gap-2">
						<h3 className="text-sm font-semibold text-text-primary">
							{run.run_name ?? "未命名评测"}
						</h3>
						<span className="rounded border border-surface-border bg-surface-card px-1.5 py-0.5 text-[11px] font-medium text-text-secondary">
							{RUN_STATUS_LABEL[run.status]}
						</span>
						<span className="rounded border border-surface-border bg-surface-card px-1.5 py-0.5 text-[11px] font-medium text-text-secondary">
							{comparisonLabel(run.comparison_role)}
						</span>
					</div>
					<p className="mt-1 text-xs leading-5 text-text-secondary">
						{datasetLabel(run.dataset_id)} · {formatDate(run.completed_at ?? run.started_at)}
					</p>
				</div>
				<div className="grid min-w-[220px] grid-cols-3 gap-2 text-center">
					<RunMetric label="意图" value={formatPercent(run.summary.intent_hit_rate)} />
					<RunMetric label="方案" value={formatPercent(run.summary.pb_hit_rate)} />
					<RunMetric label="候选" value={`${run.summary.badcase_candidate_count} 条候选`} />
				</div>
			</div>
		</article>
	);
}

function RunMetric({ label, value }: { label: string; value: string }) {
	return (
		<div className="rounded-md bg-surface-card px-2 py-2">
			<div className="text-[11px] text-text-muted">{label}</div>
			<div className="mt-1 text-xs font-semibold text-text-primary">
				{label} {value}
			</div>
		</div>
	);
}

export function BadcaseLedger() {
	const badcases = useBadcases();
	const updateBadcase = useUpdateBadcase();
	const recordRerun = useRecordBadcaseRerun();
	const executeRerun = useExecuteBadcaseRerun();
	const writebackBadcase = useWritebackBadcaseRegression();
	const autoCloseBadcase = useAutoCloseBadcase();
	const items = badcases.data?.items ?? [];
	const [selected, setSelected] = useState<BadcaseItem | null>(null);
	const handleTransition = (item: BadcaseItem, status: BadcaseItem["status"]) => {
		updateBadcase.mutate({
			badcaseId: item.badcase_id,
			body: buildBadcaseTransitionBody(item, status),
		});
	};
	const handleRerun = (
		item: BadcaseItem,
		rerunStatus: Extract<BadcaseItem["rerun_status"], "rerun_passed" | "rerun_failed">,
	) => {
		const sampleSuffix = item.sample_id?.toLowerCase() ?? "unknown";
		recordRerun.mutate({
			badcaseId: item.badcase_id,
			body: {
				rerun_status: rerunStatus,
				trace_id: `trace_m07_rerun_${sampleSuffix}`,
				admin_run_id: `run_m07_rerun_${sampleSuffix}`,
				updated_by: "admin-console",
			},
		});
	};
	const handleExecuteRerun = (item: BadcaseItem) => {
		executeRerun.mutate({
			badcaseId: item.badcase_id,
			body: {
				data_mode: "mock",
				updated_by: "admin-console",
			},
		});
	};
	const handleGraduate = (item: BadcaseItem) => {
		writebackBadcase.mutate({
			badcaseId: item.badcase_id,
			body: {
				target_dataset_id: "regression_badcase",
				target_dataset_version: "2026-05-08",
				writeback_note: "复跑通过后写回回归集，后续批量回归持续覆盖。",
				updated_by: "admin-console",
			},
		});
	};
	const handleAutoClose = (item: BadcaseItem) => {
		autoCloseBadcase.mutate({
			badcaseId: item.badcase_id,
			body: {
				close_note: "复跑通过且回归样本已写回，自动关闭进入运营收口。",
				updated_by: "admin-console",
			},
		});
	};

	return (
		<Card data-testid="m07-badcase-ledger">
			<CardHeader className="space-y-2">
				<div className="flex items-start gap-3">
					<span className="flex h-9 w-9 flex-none items-center justify-center rounded-md bg-surface-subtle text-brand-primary">
						<ClipboardList className="h-4 w-4" aria-hidden="true" />
					</span>
					<div>
						<CardTitle className="text-lg">Badcase 候选</CardTitle>
						<CardDescription className="mt-1 leading-6">
							把失败样本从“临时结果”变成可分派、可复跑、可关闭的问题资产。
						</CardDescription>
					</div>
				</div>
			</CardHeader>
			<CardContent>
				{badcases.isLoading ? (
					<p className="text-sm text-text-muted">加载 Badcase 候选…</p>
				) : badcases.isError ? (
					<p className="text-sm text-brand-danger">Badcase 候选加载失败</p>
				) : items.length === 0 ? (
					<p className="rounded-md border border-dashed border-surface-border px-3 py-5 text-sm text-text-muted">
						暂无候选。失败样本确认后会进入这里。
					</p>
				) : (
					<div className="space-y-3">
						{items.map((item) => (
							<BadcaseRow
								key={item.badcase_id}
								item={item}
								isUpdating={
									updateBadcase.isPending ||
									recordRerun.isPending ||
									executeRerun.isPending ||
									writebackBadcase.isPending ||
									autoCloseBadcase.isPending
								}
								onSelect={() => setSelected(item)}
								onTransition={(status) => handleTransition(item, status)}
								onRerun={(rerunStatus) => handleRerun(item, rerunStatus)}
								onExecuteRerun={() => handleExecuteRerun(item)}
								onGraduate={() => handleGraduate(item)}
								onAutoClose={() => handleAutoClose(item)}
							/>
						))}
					</div>
				)}

				<Dialog open={selected !== null} onOpenChange={(open) => !open && setSelected(null)}>
					<DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
						{selected ? <BadcaseDetail item={selected} /> : null}
					</DialogContent>
				</Dialog>
			</CardContent>
		</Card>
	);
}

function BadcaseRow({
	item,
	isUpdating,
	onSelect,
	onTransition,
	onRerun,
	onExecuteRerun,
	onGraduate,
	onAutoClose,
}: {
	item: BadcaseItem;
	isUpdating: boolean;
	onSelect: () => void;
	onTransition: (status: BadcaseItem["status"]) => void;
	onRerun: (
		rerunStatus: Extract<BadcaseItem["rerun_status"], "rerun_passed" | "rerun_failed">,
	) => void;
	onExecuteRerun: () => void;
	onGraduate: () => void;
	onAutoClose: () => void;
}) {
	const sampleLabel = item.sample_id ?? "未关联样本";
	return (
		<article className="rounded-md border border-surface-border bg-surface-background px-3 py-3">
			<div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
				<div className="min-w-0 space-y-2">
					<div className="flex flex-wrap items-center gap-2">
						<span className="font-mono text-xs font-semibold text-text-primary">{sampleLabel}</span>
						<span className="rounded border border-brand-warning/30 bg-brand-warning/10 px-1.5 py-0.5 text-[11px] font-semibold text-brand-warning">
							{BADCASE_STATUS_LABEL[item.status]}
						</span>
						<span className="rounded border border-surface-border bg-surface-card px-1.5 py-0.5 text-[11px] font-medium text-text-secondary">
							{item.severity}
						</span>
						{item.status === "fixed" ? (
							<span className="rounded border border-surface-border bg-surface-card px-1.5 py-0.5 text-[11px] font-medium text-text-secondary">
								{RERUN_STATUS_LABEL[item.rerun_status]}
							</span>
						) : null}
					</div>
					<p className="line-clamp-2 text-sm leading-6 text-text-primary">
						{item.message ?? "未记录客户问题"}
					</p>
					<div className="flex flex-wrap gap-2 text-xs text-text-secondary">
						<span>{ROOT_CAUSE_LABEL[item.root_cause_category]}</span>
						<span>·</span>
						<span>{responsibleAssetLabel(item)}</span>
						<span>·</span>
						<span>{item.owner ?? "待分派"}</span>
					</div>
				</div>
				<div className="flex flex-wrap items-center justify-end gap-2">
					<BadcaseAction
						item={item}
						sampleLabel={sampleLabel}
						disabled={isUpdating}
						onTransition={onTransition}
						onRerun={onRerun}
						onExecuteRerun={onExecuteRerun}
						onGraduate={onGraduate}
						onAutoClose={onAutoClose}
					/>
					<Button type="button" variant="outline" size="sm" onClick={onSelect}>
						<FileSearch className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
						查看 {sampleLabel} 详情
					</Button>
				</div>
			</div>
		</article>
	);
}

function BadcaseAction({
	item,
	sampleLabel,
	disabled,
	onTransition,
	onRerun,
	onExecuteRerun,
	onGraduate,
	onAutoClose,
}: {
	item: BadcaseItem;
	sampleLabel: string;
	disabled: boolean;
	onTransition: (status: BadcaseItem["status"]) => void;
	onRerun: (
		rerunStatus: Extract<BadcaseItem["rerun_status"], "rerun_passed" | "rerun_failed">,
	) => void;
	onExecuteRerun: () => void;
	onGraduate: () => void;
	onAutoClose: () => void;
}) {
	if (item.status === "candidate") {
		return (
			<Button type="button" size="sm" disabled={disabled} onClick={() => onTransition("confirmed")}>
				<CheckCircle2 className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
				确认 {sampleLabel}
			</Button>
		);
	}
	if (item.status === "confirmed") {
		return (
			<Button type="button" size="sm" disabled={disabled} onClick={() => onTransition("fixed")}>
				<Wrench className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
				标记 {sampleLabel} 已修复
			</Button>
		);
	}
	if (item.status === "fixed") {
		return (
			<>
				{item.rerun_status === "not_rerun" || item.rerun_status === "rerun_failed" ? (
					<Button type="button" size="sm" disabled={disabled} onClick={onExecuteRerun}>
						<RefreshCw className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
						{item.rerun_status === "rerun_failed" ? "重新发起" : "发起"} {sampleLabel} 真实复跑
					</Button>
				) : null}
				{item.rerun_status === "not_rerun" ? (
					<Button
						type="button"
						variant="outline"
						size="sm"
						disabled={disabled}
						onClick={() => onRerun("rerun_passed")}
					>
						<CheckCircle2 className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
						登记 {sampleLabel} 复跑通过
					</Button>
				) : null}
				{item.rerun_status === "not_rerun" ? (
					<Button
						type="button"
						variant="outline"
						size="sm"
						disabled={disabled}
						onClick={() => onRerun("rerun_failed")}
					>
						<AlertTriangle className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
						登记 {sampleLabel} 复跑仍失败
					</Button>
				) : null}
				{item.rerun_status === "rerun_passed" ? (
					<Button type="button" size="sm" disabled={disabled} onClick={onGraduate}>
						<CheckCircle2 className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
						写回 {sampleLabel} 到回归集
					</Button>
				) : null}
				<Button
					type="button"
					variant={item.rerun_status === "rerun_passed" ? "outline" : "default"}
					size="sm"
					disabled={disabled}
					onClick={() => onTransition("closed")}
				>
					<XCircle className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
					关闭 {sampleLabel}
				</Button>
			</>
		);
	}
	if (item.status === "graduated") {
		return (
			<Button type="button" size="sm" disabled={disabled} onClick={onAutoClose}>
				<CheckCircle2 className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
				自动关闭 {sampleLabel}
			</Button>
		);
	}
	return null;
}

function BadcaseDetail({ item }: { item: BadcaseItem }) {
	const sampleLabel = item.sample_id ?? "未关联样本";
	const events = useBadcaseEvents(item.badcase_id);
	const eventItems = events.data?.items ?? [];
	const projectionContext = buildBadcaseProjectionContext(item);
	return (
		<>
			<DialogHeader>
				<DialogTitle>Badcase {sampleLabel}</DialogTitle>
				<DialogDescription>
					默认列表只展示运营判断，技术编号和复现路径放在详情里核对。
				</DialogDescription>
			</DialogHeader>
			<div className="space-y-4 text-sm">
				<section className="rounded-md border border-surface-border bg-surface-background px-3 py-3">
					<div className="flex items-start gap-2 text-text-primary">
						<AlertTriangle className="mt-0.5 h-4 w-4 flex-none text-brand-warning" />
						<p>{item.root_cause_note ?? "待人工补充归因说明"}</p>
					</div>
				</section>
				<EvalSampleProjection
					context={projectionContext}
					actionHref={buildEvalReplayHref(projectionContext)}
					actionLabel={`用 ${sampleLabel} 进入链路测试台复跑`}
				/>
				<BadcaseTimeline
					events={eventItems}
					isError={events.isError}
					isLoading={events.isLoading}
				/>
				<dl className="grid gap-3 md:grid-cols-2">
					<Detail label="Badcase 编号" value={item.badcase_id} />
					<Detail label="评测运行" value={item.eval_run_id ?? "未关联"} />
					<Detail label="样本结果" value={item.sample_result_id ?? "未关联"} />
					<Detail label="状态" value={BADCASE_STATUS_LABEL[item.status]} />
					<Detail label="复跑结果" value={RERUN_STATUS_LABEL[item.rerun_status]} />
					<Detail label="复跑 Trace" value={item.trace_id ?? "未写回"} />
					<Detail label="复跑 Run" value={item.admin_run_id ?? "未写回"} />
					<Detail label="回归集" value={item.graduation_dataset_id ?? "未写回"} />
					<Detail
						label="写回时间"
						value={item.graduated_at ? formatDate(item.graduated_at) : "未写回"}
					/>
					<Detail label="写回说明" value={item.graduation_note ?? "未填写"} wide />
					<Detail label="根因分类" value={ROOT_CAUSE_LABEL[item.root_cause_category]} />
					<Detail label="责任资产" value={responsibleAssetLabel(item)} />
					<Detail label="建议动作" value={item.fix_suggestion ?? "待补充"} wide />
					<Detail label="负责人" value={item.owner ?? "待分派"} />
				</dl>
			</div>
		</>
	);
}

function BadcaseTimeline({
	events,
	isError,
	isLoading,
}: {
	events: BadcaseEventItem[];
	isError: boolean;
	isLoading: boolean;
}) {
	return (
		<section className="rounded-md border border-surface-border bg-surface-background px-3 py-3">
			<div className="flex items-center gap-2">
				<History className="h-4 w-4 text-brand-primary" aria-hidden="true" />
				<h3 className="text-sm font-semibold text-text-primary">状态时间线</h3>
			</div>
			{isLoading ? (
				<p className="mt-3 text-xs text-text-muted">加载状态历史…</p>
			) : isError ? (
				<p className="mt-3 text-xs text-brand-danger">状态历史加载失败</p>
			) : events.length === 0 ? (
				<p className="mt-3 text-xs text-text-muted">暂无状态历史，后续操作会在这里沉淀。</p>
			) : (
				<ol className="mt-3 space-y-2">
					{events.map((event) => (
						<li key={event.event_id} className="border-l border-surface-border pl-3">
							<div className="flex flex-wrap items-center gap-2">
								<span className="rounded border border-surface-border bg-surface-card px-1.5 py-0.5 text-[11px] font-medium text-text-secondary">
									{BADCASE_EVENT_LABEL[event.event_type]}
								</span>
								<time className="text-[11px] text-text-muted" dateTime={event.created_at}>
									{formatDate(event.created_at)}
								</time>
							</div>
							<p className="mt-1 text-sm leading-6 text-text-primary">{badcaseEventNote(event)}</p>
							{event.actor ? (
								<p className="mt-0.5 text-xs text-text-secondary">操作人：{event.actor}</p>
							) : null}
						</li>
					))}
				</ol>
			)}
		</section>
	);
}

export function EvalRunComparePanel() {
	const runs = useEvalRuns();
	const currentRun =
		runs.data?.items.find((run) => run.comparison_role === "current" && run.baseline_run_id) ??
		null;
	const compare = useEvalRunCompare(currentRun?.eval_run_id ?? null);
	const snapshots = useEvalRunCompareSnapshots(
		DEFAULT_EVAL_MERCHANT_ID,
		currentRun?.eval_run_id ?? null,
	);
	const persistCompare = usePersistEvalRunCompare(
		DEFAULT_EVAL_MERCHANT_ID,
		currentRun?.eval_run_id ?? null,
	);

	const saveCompareSnapshot = () => {
		if (!currentRun) return;
		persistCompare.mutate({
			evalRunId: currentRun.eval_run_id,
			body: {
				created_by: "admin-console",
				compare_note: "M07 页面保存 baseline/current 对比快照，作为运营复盘证据。",
			},
		});
	};

	return (
		<Card data-testid="m07-compare-panel">
			<CardHeader className="space-y-2">
				<div className="flex items-start gap-3">
					<span className="flex h-9 w-9 flex-none items-center justify-center rounded-md bg-surface-subtle text-brand-primary">
						<GitCompareArrows className="h-4 w-4" aria-hidden="true" />
					</span>
					<div>
						<CardTitle className="text-lg">baseline/current 对比</CardTitle>
						<CardDescription className="mt-1 leading-6">
							把修复前后的同一批样本放在一起看，先判断效果是否靠近目标，再决定是否继续扩展修复。
						</CardDescription>
					</div>
				</div>
			</CardHeader>
			<CardContent>
				{runs.isLoading || compare.isLoading ? (
					<p className="text-sm text-text-muted">加载评测对比…</p>
				) : runs.isError || compare.isError ? (
					<p className="text-sm text-brand-danger">评测对比加载失败</p>
				) : !currentRun ? (
					<p className="rounded-md border border-dashed border-surface-border px-3 py-5 text-sm text-text-muted">
						暂无可对比的 current 运行。先把一次修复后复跑关联到 baseline。
					</p>
				) : compare.data ? (
					<CompareContent
						compare={compare.data}
						isSnapshotError={snapshots.isError}
						isSaving={persistCompare.isPending}
						latestSnapshot={snapshots.data?.items[0] ?? null}
						onSaveSnapshot={saveCompareSnapshot}
						saveError={persistCompare.isError}
						saveSuccess={persistCompare.isSuccess}
					/>
				) : null}
			</CardContent>
		</Card>
	);
}

export function RegressionBatchPanel() {
	const targetDatasetId = "regression_badcase";
	const batches = useRegressionBatches(DEFAULT_EVAL_MERCHANT_ID, targetDatasetId, 5);
	const scheduleBatch = useScheduleRegressionBatch();
	const executeBatch = useExecuteRegressionBatch();
	const closeBatchBadcases = useCloseRegressionBatchBadcases();
	const items = batches.data?.items ?? [];
	const latestBatch = executeBatch.data?.batch ?? scheduleBatch.data ?? items[0] ?? null;
	const handleSchedule = () => {
		scheduleBatch.mutate({
			merchant_id: DEFAULT_EVAL_MERCHANT_ID,
			target_dataset_id: targetDatasetId,
			target_dataset_version: "2026-05-08",
			mode: "both",
			data_mode: "mock",
			limit: 50,
			created_by: "admin-console",
			schedule_note: "把已写回回归集的样本纳入下一轮批量验证。",
		});
	};
	const handleExecute = () => {
		if (!latestBatch) return;
		executeBatch.mutate({
			batchId: latestBatch.batch_id,
			body: {
				executed_by: "admin-console",
				execution_note: "执行已写回样本的批量回归，生成本轮评测结果。",
			},
		});
	};
	const handleBatchClose = () => {
		if (!latestBatch) return;
		closeBatchBadcases.mutate({
			batchId: latestBatch.batch_id,
			body: {
				dry_run: false,
				updated_by: "admin-console",
				close_note: "本轮批量回归通过，批量关闭符合条件的 Badcase。",
			},
		});
	};
	const canExecute = latestBatch?.status === "scheduled";
	const canBatchClose = latestBatch?.status === "completed" && Boolean(latestBatch.eval_run_id);
	const closeSummary = closeBatchBadcases.data;

	return (
		<Card data-testid="m07-regression-batch-panel">
			<CardHeader className="space-y-2">
				<div className="flex items-start gap-3">
					<span className="flex h-9 w-9 flex-none items-center justify-center rounded-md bg-surface-subtle text-brand-primary">
						<CalendarClock className="h-4 w-4" aria-hidden="true" />
					</span>
					<div>
						<CardTitle className="text-lg">批量回归调度</CardTitle>
						<CardDescription className="mt-1 leading-6">
							把已写回回归集的样本组成下一轮验证任务，先确认覆盖范围，再进入实际执行。
						</CardDescription>
					</div>
				</div>
			</CardHeader>
			<CardContent>
				<div className="flex flex-col gap-3 rounded-md border border-surface-border bg-surface-background px-3 py-3 md:flex-row md:items-center md:justify-between">
					<div>
						<p className="text-sm font-semibold text-text-primary">
							{latestBatch ? "最近任务" : "暂无批量回归任务"}
						</p>
						<p className="mt-1 text-xs leading-5 text-text-secondary">
							{batches.isLoading
								? "正在读取批量任务记录。"
								: batches.isError
									? "批量任务记录加载失败，当前页面其他评测信息不受影响。"
									: latestBatch
										? `${latestBatch.sample_count} 条样本，${REGRESSION_BATCH_STATUS_LABEL[latestBatch.status]} · ${formatDate(latestBatch.created_at)}`
										: "已写回的样本还没有组成批量验证任务。"}
						</p>
						{scheduleBatch.isSuccess ? (
							<p className="mt-1 text-xs font-medium text-brand-success">已生成批量回归任务</p>
						) : null}
						{executeBatch.isSuccess ? (
							<p className="mt-1 text-xs font-medium text-brand-success">
								已执行批量回归，评测运行历史已更新
							</p>
						) : null}
						{closeSummary ? (
							<p className="mt-1 text-xs font-medium text-brand-success">
								已批量关闭 {closeSummary.closed_count} 条 Badcase，保留 {closeSummary.skipped_count}{" "}
								条继续观察
							</p>
						) : null}
						{scheduleBatch.isError ? (
							<p className="mt-1 text-xs font-medium text-brand-danger">
								批量回归任务生成失败，请先确认已有样本写回回归集。
							</p>
						) : null}
						{executeBatch.isError ? (
							<p className="mt-1 text-xs font-medium text-brand-danger">
								批量回归执行失败，请确认任务仍处于待执行状态。
							</p>
						) : null}
						{closeBatchBadcases.isError ? (
							<p className="mt-1 text-xs font-medium text-brand-danger">
								批量关闭失败，请确认任务已完成且样本仍满足关闭条件。
							</p>
						) : null}
					</div>
					<div className="flex w-full flex-col gap-2 md:w-auto md:flex-row">
						<Button
							type="button"
							size="sm"
							variant="outline"
							className="w-full gap-2 md:w-auto"
							onClick={handleSchedule}
							disabled={scheduleBatch.isPending}
						>
							<CalendarClock className="h-4 w-4" aria-hidden="true" />
							{scheduleBatch.isPending ? "生成中" : "生成批量回归任务"}
						</Button>
						<Button
							type="button"
							size="sm"
							className="w-full gap-2 md:w-auto"
							onClick={handleExecute}
							disabled={!canExecute || executeBatch.isPending}
						>
							<PlayCircle className="h-4 w-4" aria-hidden="true" />
							{executeBatch.isPending ? "执行中" : "执行批量回归"}
						</Button>
						<Button
							type="button"
							size="sm"
							className="w-full gap-2 md:w-auto"
							onClick={handleBatchClose}
							disabled={!canBatchClose || closeBatchBadcases.isPending}
						>
							<CheckCircle2 className="h-4 w-4" aria-hidden="true" />
							{closeBatchBadcases.isPending ? "关闭中" : "批量关闭符合条件 Badcase"}
						</Button>
					</div>
				</div>
			</CardContent>
		</Card>
	);
}

function CompareContent({
	compare,
	isSaving,
	isSnapshotError,
	latestSnapshot,
	onSaveSnapshot,
	saveError,
	saveSuccess,
}: {
	compare: EvalRunCompareResponse;
	isSaving: boolean;
	isSnapshotError: boolean;
	latestSnapshot: EvalRunCompareSnapshotItem | null;
	onSaveSnapshot: () => void;
	saveError: boolean;
	saveSuccess: boolean;
}) {
	const intentDelta = compare.metric_deltas.intent_hit_rate?.delta ?? 0;
	const pbDelta = compare.metric_deltas.pb_hit_rate?.delta ?? 0;
	const visibleChanges = compare.sample_deltas.filter(
		(item) => item.change_type === "fixed" || item.change_type === "regressed",
	);
	return (
		<div className="space-y-4">
			<div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
				<div>
					<p className="text-sm font-semibold text-text-primary">当前版本较基线</p>
					<p className="mt-1 text-xs leading-5 text-text-secondary">
						{datasetLabel(compare.current.dataset_id)} ·{" "}
						{compare.dataset_match ? "同数据集可比" : "数据集存在差异，需谨慎解读"}
					</p>
				</div>
				<div className="grid min-w-[280px] grid-cols-2 gap-2 text-center md:grid-cols-4">
					<RunMetric label="意图" value={formatPointDelta(intentDelta)} />
					<RunMetric label="方案" value={formatPointDelta(pbDelta)} />
					<RunMetric label="修复" value={`${compare.fixed_count} 条`} />
					<RunMetric label="回退" value={`${compare.regressed_count} 条`} />
				</div>
			</div>
			<section
				className="flex flex-col gap-3 rounded-md border border-surface-border bg-surface-background px-3 py-3 md:flex-row md:items-center md:justify-between"
				data-testid="m07-compare-snapshot"
			>
				<div>
					<p className="text-sm font-semibold text-text-primary">对比快照</p>
					<p className="mt-1 text-xs leading-5 text-text-secondary">
						{latestSnapshot
							? `最近快照：${formatDate(latestSnapshot.created_at)} 保存，修复 ${latestSnapshot.fixed_count} 条，回退 ${latestSnapshot.regressed_count} 条`
							: "当前对比尚未保存为复盘快照。"}
					</p>
					{isSnapshotError ? (
						<p className="mt-1 text-xs text-brand-danger">快照记录加载失败，当前对比不受影响。</p>
					) : null}
					{saveSuccess ? (
						<p className="mt-1 text-xs font-medium text-brand-success">已保存对比快照</p>
					) : null}
					{saveError ? (
						<p className="mt-1 text-xs font-medium text-brand-danger">对比快照保存失败</p>
					) : null}
				</div>
				<Button
					type="button"
					variant="outline"
					size="sm"
					className="w-full gap-2 md:w-auto"
					onClick={onSaveSnapshot}
					disabled={isSaving}
				>
					<Save className="h-4 w-4" aria-hidden="true" />
					{isSaving ? "保存中" : "保存对比快照"}
				</Button>
			</section>
			{visibleChanges.length > 0 ? (
				<div className="grid gap-2 md:grid-cols-2">
					{visibleChanges.map((item) => (
						<SampleDeltaRow key={item.sample_id} item={item} />
					))}
				</div>
			) : (
				<p className="rounded-md border border-dashed border-surface-border px-3 py-4 text-sm text-text-muted">
					本次对比没有新增修复或回退样本。
				</p>
			)}
		</div>
	);
}

function SampleDeltaRow({ item }: { item: EvalRunCompareResponse["sample_deltas"][number] }) {
	const isFixed = item.change_type === "fixed";
	return (
		<article className="rounded-md border border-surface-border bg-surface-background px-3 py-3">
			<div className="flex flex-wrap items-center gap-2">
				<span className="font-mono text-xs font-semibold text-text-primary">{item.sample_id}</span>{" "}
				<span
					className={
						isFixed
							? "rounded border border-brand-success/30 bg-brand-success/10 px-1.5 py-0.5 text-[11px] font-semibold text-brand-success"
							: "rounded border border-brand-danger/30 bg-brand-danger/10 px-1.5 py-0.5 text-[11px] font-semibold text-brand-danger"
					}
				>
					{isFixed ? "已修复" : "新增回退"}
				</span>
			</div>
			<p className="mt-2 line-clamp-2 text-sm leading-6 text-text-primary">
				{item.message ?? "未记录客户问题"}
			</p>
			<p className="mt-2 text-xs text-text-secondary">
				意图 {matchLabel(item.baseline_intent_match)} → {matchLabel(item.current_intent_match)}
				<span className="mx-1">·</span>
				方案 {matchLabel(item.baseline_pb_match)} → {matchLabel(item.current_pb_match)}
			</p>
		</article>
	);
}

function Detail({ label, value, wide = false }: { label: string; value: string; wide?: boolean }) {
	return (
		<div className={wide ? "md:col-span-2" : undefined}>
			<dt className="text-xs font-medium text-text-muted">{label}</dt>
			<dd className="mt-1 break-words font-mono text-xs text-text-primary">{value}</dd>
		</div>
	);
}

function formatPercent(value: number) {
	return `${(value * 100).toFixed(1)}%`;
}

function formatPointDelta(value: number) {
	const points = value * 100;
	if (Math.abs(points) < 0.05) return "0.0pp";
	const sign = points > 0 ? "+" : "";
	return `${sign}${points.toFixed(1)}pp`;
}

function formatDate(value: string) {
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return value;
	return new Intl.DateTimeFormat("zh-CN", {
		month: "2-digit",
		day: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
	}).format(date);
}

function datasetLabel(datasetId: string) {
	if (datasetId.includes("golden15")) return "Golden15 · 代表性样本";
	return datasetId;
}

function comparisonLabel(role: EvalRunItem["comparison_role"]) {
	if (role === "baseline") return "基线";
	if (role === "current") return "当前版本";
	return "独立运行";
}

function responsibleAssetLabel(item: BadcaseItem) {
	if (item.responsible_asset_type === "intent_rule") return "M02 意图规则";
	if (item.responsible_asset_type === "playbook_mapping") return "M03 方案映射";
	if (item.responsible_asset_type === "skill") return "M04 Skill";
	if (item.responsible_asset_type === "knowledge") return "M09 知识";
	return item.responsible_asset_id ?? "责任资产待确认";
}

function buildBadcaseProjectionContext(item: BadcaseItem): EvalSampleProjectionContext {
	const note = item.root_cause_note ?? "";
	return {
		sourceLabel: "M07 Badcase",
		sampleId: item.sample_id,
		message: item.message,
		expectedIntent: extractNoteValue(note, /期望\s*([^，；]+)/),
		actualIntent: extractNoteValue(note, /实际\s*([^，；]+)/),
		expectedPb: extractNoteValue(note, /方案期望\s*([^，；]+)/),
		actualPb: normalizeNullableValue(
			extractNoteValue(note, /方案期望\s*[^，；]+，实际\s*([^，；]+)/),
		),
		rootCauseHint: ROOT_CAUSE_LABEL[item.root_cause_category],
		responsibleAsset: responsibleAssetLabel(item),
		badcaseReason: item.root_cause_note,
		evalRunId: item.eval_run_id,
		sampleResultId: item.sample_result_id,
		badcaseId: item.badcase_id,
		statusLabel: BADCASE_STATUS_LABEL[item.status],
		severity: item.severity,
		rerunStatusLabel: RERUN_STATUS_LABEL[item.rerun_status],
		traceId: item.trace_id,
		adminRunId: item.admin_run_id,
	};
}

function extractNoteValue(note: string, pattern: RegExp): string | null {
	const value = note.match(pattern)?.[1]?.trim();
	return normalizeNullableValue(value);
}

function normalizeNullableValue(value: string | null | undefined): string | null {
	if (!value || value === "无" || value === "null") return null;
	return value;
}

function badcaseEventNote(event: BadcaseEventItem) {
	if (event.event_note) return event.event_note;
	if (event.event_type === "candidate_created") return "Badcase 候选已创建";
	if (event.event_type === "status_changed" && event.from_status && event.to_status) {
		return `Badcase 状态从 ${BADCASE_STATUS_LABEL[event.from_status]} 变为 ${
			BADCASE_STATUS_LABEL[event.to_status]
		}`;
	}
	if (event.event_type === "rerun_recorded" && event.rerun_status) {
		return `登记复跑结果：${RERUN_STATUS_LABEL[event.rerun_status]}`;
	}
	if (event.event_type === "regression_writeback") return "Badcase 已写回回归集";
	if (event.event_type === "graduated") return "Badcase 已登记毕业到回归集";
	return BADCASE_EVENT_LABEL[event.event_type];
}

function buildBadcaseTransitionBody(
	item: BadcaseItem,
	status: BadcaseItem["status"],
): BadcaseUpdateRequest {
	const base = { status, updated_by: "admin-console" } satisfies BadcaseUpdateRequest;
	if (status === "confirmed") {
		return {
			...base,
			owner: item.owner && item.owner !== "运营待分派" ? item.owner : "运营已确认",
			root_cause_note: item.root_cause_note ?? "运营已确认该样本进入 Badcase 修复流转。",
		};
	}
	if (status === "fixed") {
		return {
			...base,
			fix_suggestion: item.fix_suggestion ?? "已完成对应配置修复，等待复跑验证。",
		};
	}
	return base;
}

function matchLabel(value: boolean | null) {
	if (value === true) return "命中";
	if (value === false) return "未命中";
	return "未评";
}
